import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useGetAdditionalInformationEnums,
  useUpatePersonAdditionalInfo
} from '@common/slices';
import { IPersonAdditionalInformationDrawerProps } from '@common/types';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import {
  getInputs,
  initialValues
} from '@modules/salesforce/persons/person-details-page/person-additional-information-drawer/content';
import {
  graphqlEntityToAdditionalInfo,
  LookupToList
} from '@modules/salesforce/persons/person-details-page/person-additional-information-drawer/utils';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { getError } from '@utils/graph-utils';

import { isEmpty } from 'lodash';

const AdditionalInformationDrawer: React.FC<
  IPersonAdditionalInformationDrawerProps
> = ({ personDetails, personId, personType, open, onClose }) => {
  const navigate = useNavigate();
  const { data } = useGetAdditionalInformationEnums();

  const [booted, setBooted] = useState<boolean>(false);
  const [values, setValues] = useState<Record<string, any>>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [inputForm, setInputForm] = useState<IDynamicFormWidgetSection[]>(
    getInputs(values || (initialValues as any), lovs, personType)
  );

  const [updatePersonAdditionalInfo] = useUpatePersonAdditionalInfo(personId);

  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const loadLovList = async () => {
    const newAdditionalInformationEnums = LookupToList(data);

    return newAdditionalInformationEnums;
  };

  const getEntityInfo = async () => {
    if (personId) {
      const additionalInformationEntity = graphqlEntityToAdditionalInfo(
        personDetails as any
      );

      return additionalInformationEntity;
    }

    return null;
  };

  const initialize = async () => {
    try {
      const [additionalInfoEntity, lovData] = await Promise.all([
        getEntityInfo(),
        loadLovList()
      ]);

      setLovs(lovData);
      setValues({ ...additionalInfoEntity });
      setInputForm(getInputs(additionalInfoEntity, lovData, personType));
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    if (data) initialize();
  }, [data]);

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      getInputs(values as any, lovs, personType),
      values
    );

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variables = {
        input: {
          id: personId || null,
          gender: data.gender || null,
          maritalStatus: data.maritalStatus || null,
          fatherName: data.fatherName || null,
          motherName: data.motherName || null,
          countryOfBirth: data.countryOfBirth || null,
          cityOfBirth: data.cityOfBirth || null,
          nationalityID: data.nationalityID || null,
          passportNumber: data.passportNumber || null,
          secondNationality: data.secondNationality || null,
          spouseFullName: data.spouseFullName || null,
          spouseProfession: data.spouseProfession || null,
          civilRegisterPlace: data.civilRegisterPlace || null,
          civilRegisterNumber: data.civilRegisterNumber || null
        }
      };

      const res = await updatePersonAdditionalInfo(variables);

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            Person was modified successfully
          </ToastSuccessMessage>
        );

        setSubmitButtonState('success');

        setTimeout(() => {
          onClose();

          if (!personId) {
            const newPersonId =
              res.data.salesforceManagement.actions.createPerson.id;
            navigate(`/salesforce/persons/` + newPersonId);
          }
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title="Modify Additional Information"
      onClose={() => onClose()}
      isOpen={open}
      loading={!booted}
      sections={inputForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      isSubmitButtonDisabled={!!submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export { AdditionalInformationDrawer };
